import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ARCHETYPE_META, ArchetypeIcon } from './NeuralProfile';
import { getProfile, getMatchup, trackChallengeEvent } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DIMENSION_LABELS = {
  rawSpeed:         'Reaction Speed',
  decisionQuality:  'Decision Quality',
  aimPrecision:     'Aim Precision',
  trackingAccuracy: 'Target Tracking',
  workingMemory:    'Working Memory',
  consistency:      'Consistency',
  flickSpeed:       'Flick Speed',
  prioritization:   'Prioritization',
};

export default function ChallengeView() {
  const { shareableId } = useParams();
  const navigate        = useNavigate();
  const { user }        = useAuth();
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [copied,   setCopied]   = useState(false);
  const [matchup,  setMatchup]  = useState(null);

  useEffect(() => {
    document.title = 'Challenge | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);
  useEffect(() => {
    getProfile(shareableId)
      .then(data => {
        setProfile(data);
        setLoading(false);
        trackChallengeEvent('view', shareableId).catch(() => {});
        // Fetch matchup analysis — pass viewer's archetype if they have a profile
        const viewerArchetype = user?.claimedProfiles?.length
          ? null // will be enriched below if we have their profile
          : null;
        getMatchup(shareableId, viewerArchetype).then(d => setMatchup(d.matchup)).catch(() => {});
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [shareableId]); // eslint-disable-line

  const handleAccept = () => {
    sessionStorage.setItem('nr_challenge_id', shareableId);
    trackChallengeEvent('accept', shareableId).catch(() => {});
    navigate('/');
  };

  const copyChallenge = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#060a0f' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading challenge...</div>
    </div>
  );

  if (error || !profile) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#060a0f' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: 12 }}>Challenge not found.</div>
        <button className="btn btn--primary" onClick={() => navigate('/')}>Take the Combine</button>
      </div>
    </div>
  );

  const meta         = ARCHETYPE_META[profile.archetype?.id] || { color: '#00d4ff', icon: '◈' };
  const scores       = profile.scores || {};
  const dimEntries   = Object.entries(scores).filter(([k]) => k !== 'overallScore');
  const overallScore = scores.overallScore ?? Math.round(dimEntries.reduce((a, [, v]) => a + v, 0) / (dimEntries.length || 1));
  const topDims      = [...dimEntries].sort(([, a], [, b]) => b - a).slice(0, 3);
  const playerTag    = profile.playerTag || shareableId;

  // Generate challenge tweet text
  const challengeUrl  = window.location.href;
  const tweetText     = `${playerTag} just challenged me on NeuroRank. They scored ${overallScore}/100 as a ${profile.archetype?.name}. Can you beat it?`;
  const tweetUrl      = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(challengeUrl)}`;

  return (
    <div style={{
      minHeight: '100vh', background: '#060a0f',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '40px 20px',
    }}>

      {/* Header */}
      <div style={{ marginBottom: 8, fontSize: 11, letterSpacing: 3, color: 'var(--text-muted)', fontWeight: 700 }}>
        NEURORANK — CHALLENGE
      </div>

      {/* Challenge card */}
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#0d1421',
        border: `2px solid ${meta.color}44`,
        borderRadius: 20,
        padding: '36px 32px',
        boxShadow: `0 0 60px ${meta.color}18, 0 0 0 1px ${meta.color}22`,
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
        marginBottom: 24,
      }}>
        {/* Glow */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: `radial-gradient(ellipse at top, ${meta.color}0a, transparent 60%)`,
          pointerEvents: 'none',
        }} />

        {/* Challenger info */}
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: 1 }}>
          CHALLENGE FROM
        </div>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 4 }}>{playerTag}</div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: `${meta.color}18`, border: `1px solid ${meta.color}44`,
          borderRadius: 20, padding: '4px 14px', marginBottom: 28,
          fontSize: 13, fontWeight: 700, color: meta.color,
        }}>
          <ArchetypeIcon archetypeId={profile.archetype?.id} size={14} /> {profile.archetype?.name}
        </div>

        {/* Score vs beat target */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: 2 }}>
            SCORE TO BEAT
          </div>
          <div style={{
            fontSize: 72, fontWeight: 900, lineHeight: 1,
            color: meta.color,
            textShadow: `0 0 40px ${meta.color}66`,
          }}>
            {overallScore}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>/100</div>
        </div>

        {/* Top dimensions */}
        <div style={{ textAlign: 'left', marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 2, marginBottom: 12 }}>
            THEIR STRONGEST ATTRIBUTES
          </div>
          {topDims.map(([key, val]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {DIMENSION_LABELS[key] || key}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{val}</span>
              </div>
              <div style={{ height: 3, background: 'var(--border)', borderRadius: 2 }}>
                <div style={{
                  height: '100%', width: `${val}%`, borderRadius: 2,
                  background: `linear-gradient(90deg, ${meta.color}88, ${meta.color})`,
                  boxShadow: `0 0 8px ${meta.color}66`,
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* Accept challenge CTA */}
        <button
          className="btn btn--primary"
          style={{ width: '100%', padding: '14px 0', fontSize: 15, fontWeight: 800, marginBottom: 10, letterSpacing: 0.5 }}
          onClick={handleAccept}
        >
          ⚔ Accept Challenge
        </button>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Takes 10 minutes · Free · No account required
        </div>
      </div>

      {/* Matchup analysis */}
      {matchup && (
        <div style={{
          width: '100%', maxWidth: 480, marginBottom: 16,
          background: '#0d1421', border: '1px solid #1a2a3a',
          borderRadius: 12, padding: '14px 18px',
        }}>
          <div style={{ fontSize: 10, color: meta.color, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            Intel Report
          </div>
          <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.7 }}>{matchup}</div>
        </div>
      )}

      {/* Share this challenge */}
      <div style={{ display: 'flex', gap: 10, width: '100%', maxWidth: 480 }}>
        <button
          onClick={copyChallenge}
          style={{
            flex: 1, padding: '10px 0', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: copied ? 'var(--accent)18' : 'transparent',
            border: '1px solid var(--border)', color: 'var(--text-secondary)',
            transition: 'all 0.15s',
          }}
        >
          {copied ? '✓ Link Copied' : 'Copy Challenge Link'}
        </button>
        <a
          href={tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1, padding: '10px 0', borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: '#1d9bf011', border: '1px solid #1d9bf033', color: '#1d9bf0',
            textDecoration: 'none', textAlign: 'center',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#1d9bf022'}
          onMouseLeave={e => e.currentTarget.style.background = '#1d9bf011'}
        >
          Share on X
        </a>
      </div>

      {/* View their full profile */}
      <button
        onClick={() => navigate(`/profile/${shareableId}`)}
        style={{
          marginTop: 16, background: 'none', border: 'none',
          color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        View full profile
      </button>

    </div>
  );
}
